import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Plus, Edit2, Trash2, UserCog, Upload, Camera, X, Globe, Eye, EyeOff } from "lucide-react";

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Module-level helpers — must live OUTSIDE the component so they're available
// before useState(defaultForm()) is evaluated
function defaultSchedule() {
  return DAYS.reduce((acc, d) => {
    acc[d] = { start: '09:00', end: '17:00', enabled: d !== 'sunday' };
    return acc;
  }, {});
}

function defaultForm() {
  return {
    name: '', email: '', phone: '',
    role: 'staff', job_title: '',
    bio: '', photo_url: '',
    portfolio: [], languages: [],
    service_ids: [], show_on_public: true,
    active: true, schedule: defaultSchedule()
  };
}

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [form, setForm] = useState(defaultForm());
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
  const photoRef = useRef();
  const portfolioRef = useRef();

  const load = async () => {
    try {
      const [staffRes, svcRes] = await Promise.all([
        axios.get(`${API}/staff`),
        axios.get(`${API}/services`)
      ]);
      setStaff(staffRes.data);
      setServices(svcRes.data);
    } catch (e) {  }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(defaultForm());
    setActiveTab('profile');
    setDialog('new');
  };

  const openEdit = (s) => {
    setForm({
      ...defaultForm(),
      ...s,
      schedule: s.schedule || defaultSchedule(),
      portfolio: s.portfolio || [],
      languages: s.languages || [],
      service_ids: s.service_ids || [],
    });
    setActiveTab('profile');
    setDialog(s);
  };

  const save = async () => {
    try {
      const payload = { ...form };
      // Don't send photo_url via this endpoint if it's a data URI — handled by upload-photo
      if (dialog === 'new') {
        await axios.post(`${API}/staff`, payload);
        toast.success('Staff member added');
      } else {
        await axios.put(`${API}/staff/${dialog.id}`, payload);
        toast.success('Staff updated');
      }
      setDialog(null);
      load();
    } catch (e) { toast.error('Failed to save'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try {
      await axios.delete(`${API}/staff/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const uploadPhoto = async (file) => {
    if (!file || dialog === 'new') {
      // For new staff, just preview locally
      const reader = new FileReader();
      reader.onload = e => setForm(p => ({ ...p, photo_url: e.target.result }));
      reader.readAsDataURL(file);
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/staff/${dialog.id}/upload-photo`, fd);
      setForm(p => ({ ...p, photo_url: res.data.photo_url }));
      setStaff(prev => prev.map(s => s.id === dialog.id ? { ...s, photo_url: res.data.photo_url } : s));
      toast.success('Photo updated');
    } catch (e) { toast.error('Upload failed'); }
    setUploadingPhoto(false);
  };

  const uploadPortfolioPhoto = async (file) => {
    if (!file) return;
    if (dialog === 'new') {
      const reader = new FileReader();
      reader.onload = e => {
        setForm(p => ({ ...p, portfolio: [...(p.portfolio || []), { id: Date.now().toString(), photo_url: e.target.result, caption: '' }] }));
      };
      reader.readAsDataURL(file);
      return;
    }
    setUploadingPortfolio(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/staff/${dialog.id}/portfolio`, fd);
      setForm(p => ({ ...p, portfolio: [...(p.portfolio || []), res.data.photo] }));
      toast.success('Photo added to portfolio');
    } catch (e) { toast.error('Upload failed'); }
    setUploadingPortfolio(false);
  };

  const removePortfolioPhoto = async (photoId) => {
    if (dialog !== 'new') {
      try {
        await axios.delete(`${API}/staff/${dialog.id}/portfolio/${photoId}`);
      } catch { /* portfolio delete - non-critical */ }
    }
    setForm(p => ({ ...p, portfolio: (p.portfolio || []).filter(ph => ph.id !== photoId) }));
  };

  const toggleService = (svcId) => {
    setForm(p => ({
      ...p,
      service_ids: p.service_ids?.includes(svcId)
        ? p.service_ids.filter(id => id !== svcId)
        : [...(p.service_ids || []), svcId]
    }));
  };

  const toggleLanguage = (lang) => {
    setForm(p => ({
      ...p,
      languages: p.languages?.includes(lang)
        ? p.languages.filter(l => l !== lang)
        : [...(p.languages || []), lang]
    }));
  };

  const updateScheduleDay = (day, field, value) => {
    setForm(p => ({
      ...p,
      schedule: { ...p.schedule, [day]: { ...p.schedule[day], [field]: value } }
    }));
  };

  // Group services by category for the checklist
  const servicesByCategory = services.reduce((acc, svc) => {
    const cat = svc.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(svc);
    return acc;
  }, {});

  const COMMON_LANGUAGES = ['English', 'Polish', 'Spanish', 'French', 'Portuguese', 'Romanian', 'Lithuanian', 'Slovak'];

  return (
    <div className="space-y-4 fade-in">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Staff</h1>
          <p className="text-sm text-muted-foreground">{staff.length} team members</p>
        </div>
        <Button onClick={openNew} className="rounded-xl">
          <Plus size={16} className="mr-1" /> Add Staff
        </Button>
      </div>

      {/* Staff cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {staff.map(s => (
          <Card key={s.id} className={`p-4 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] card-hover ${!s.active ? 'opacity-50' : ''}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                {s.photo_url ? (
                  <img src={s.photo_url} alt={s.name} className="w-12 h-12 rounded-full object-cover border-2 border-border" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                    <UserCog size={20} className="text-primary" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>{s.name}</h3>
                  <p className="text-xs text-muted-foreground">{s.job_title || s.role}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {s.show_on_public !== false ? (
                      <Badge className="text-[9px] bg-green-100 text-green-700 rounded-full px-1.5 py-0"><Eye size={9} className="mr-0.5" />Public</Badge>
                    ) : (
                      <Badge className="text-[9px] bg-gray-100 text-gray-500 rounded-full px-1.5 py-0"><EyeOff size={9} className="mr-0.5" />Hidden</Badge>
                    )}
                    {(s.portfolio || []).length > 0 && (
                      <Badge className="text-[9px] bg-blue-100 text-blue-700 rounded-full px-1.5 py-0">{s.portfolio.length} photos</Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(s)} className="h-7 w-7 rounded-lg"><Edit2 size={12} /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(s.id)} className="h-7 w-7 rounded-lg text-destructive"><Trash2 size={12} /></Button>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {DAYS.map(d => {
                const ds = (s.schedule || {})[d];
                return (
                  <span key={d} className={`text-[9px] px-1.5 py-0.5 rounded ${ds?.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                    {d.slice(0, 3)}
                  </span>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      {/* Edit / Add Dialog */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display' }}>{dialog === 'new' ? 'Add Staff Member' : `Edit — ${form.name}`}</DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="rounded-xl mb-4">
              <TabsTrigger value="profile" className="rounded-lg text-xs">Profile</TabsTrigger>
              <TabsTrigger value="services" className="rounded-lg text-xs">Services</TabsTrigger>
              <TabsTrigger value="portfolio" className="rounded-lg text-xs">Portfolio {form.portfolio?.length > 0 && `(${form.portfolio.length})`}</TabsTrigger>
              <TabsTrigger value="schedule" className="rounded-lg text-xs">Schedule</TabsTrigger>
            </TabsList>

            {/* ── Profile Tab ── */}
            <TabsContent value="profile" className="space-y-4 mt-0">
              {/* Photo upload */}
              <div className="flex flex-col items-center gap-3">
                <div className="relative">
                  {form.photo_url ? (
                    <img src={form.photo_url} alt="Profile" className="w-24 h-24 rounded-full object-cover border-2 border-primary/30" />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-secondary flex items-center justify-center border-2 border-dashed border-border">
                      <Camera size={28} className="text-muted-foreground" />
                    </div>
                  )}
                  <button
                    onClick={() => photoRef.current?.click()}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center shadow-md hover:bg-primary/80 transition-colors"
                    disabled={uploadingPhoto}
                  >
                    <Upload size={13} />
                  </button>
                  <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files?.[0]) uploadPhoto(e.target.files[0]); }} />
                </div>
                <p className="text-xs text-muted-foreground">Click the upload button to add a profile photo</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Full Name</Label>
                  <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="mt-1 rounded-xl" placeholder="e.g. Elke Dalla Venezia" />
                </div>
                <div>
                  <Label className="text-xs">Job Title (shown publicly)</Label>
                  <Input value={form.job_title || ''} onChange={e => setForm(p => ({...p, job_title: e.target.value}))} className="mt-1 rounded-xl" placeholder="e.g. Owner & Nail Technician" />
                </div>
                <div>
                  <Label className="text-xs">Internal Role</Label>
                  <Input value={form.role} onChange={e => setForm(p => ({...p, role: e.target.value}))} className="mt-1 rounded-xl" placeholder="owner / staff / manager" />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input value={form.email || ''} onChange={e => setForm(p => ({...p, email: e.target.value}))} className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={form.phone || ''} onChange={e => setForm(p => ({...p, phone: e.target.value}))} className="mt-1 rounded-xl" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">About Me / Bio (shown on public profile)</Label>
                  <Textarea
                    value={form.bio || ''}
                    onChange={e => setForm(p => ({...p, bio: e.target.value}))}
                    className="mt-1 rounded-xl"
                    rows={4}
                    placeholder="Tell clients about your experience, specialities, and what makes you unique..."
                  />
                </div>
              </div>

              {/* Languages */}
              <div>
                <Label className="text-xs mb-2 block">Languages spoken</Label>
                <div className="flex flex-wrap gap-2">
                  {COMMON_LANGUAGES.map(lang => (
                    <button
                      key={lang}
                      onClick={() => toggleLanguage(lang)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        form.languages?.includes(lang)
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>

              {/* Visibility + Active */}
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Switch checked={form.show_on_public !== false} onCheckedChange={v => setForm(p => ({...p, show_on_public: v}))} />
                  <Label className="text-xs">Show on public booking page</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.active} onCheckedChange={v => setForm(p => ({...p, active: v}))} />
                  <Label className="text-xs">Active</Label>
                </div>
              </div>
            </TabsContent>

            {/* ── Services Tab ── */}
            <TabsContent value="services" className="mt-0">
              {/* Global select/deselect all */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-muted-foreground">Tick the services this team member performs. These show on their public profile.</p>
                <button
                  onClick={() => {
                    const allSelected = services.every(s => form.service_ids?.includes(s.id));
                    setForm(p => ({ ...p, service_ids: allSelected ? [] : services.map(s => s.id) }));
                  }}
                  className="text-xs font-medium text-primary hover:underline shrink-0 ml-3"
                >
                  {services.every(s => form.service_ids?.includes(s.id)) ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              {Object.entries(servicesByCategory).map(([cat, svcs]) => {
                const allCatSelected = svcs.every(s => form.service_ids?.includes(s.id));
                return (
                <div key={cat} className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{cat}</p>
                    <button
                      onClick={() => {
                        const ids = svcs.map(s => s.id);
                        setForm(p => ({
                          ...p,
                          service_ids: allCatSelected
                            ? (p.service_ids || []).filter(id => !ids.includes(id))
                            : [...new Set([...(p.service_ids || []), ...ids])]
                        }));
                      }}
                      className="text-[10px] text-primary hover:underline"
                    >
                      {allCatSelected ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {svcs.map(svc => (
                      <div key={svc.id} className="flex items-center gap-3 p-2.5 rounded-xl border hover:bg-muted/20 transition-colors">
                        <Checkbox
                          id={`svc-${svc.id}`}
                          checked={form.service_ids?.includes(svc.id)}
                          onCheckedChange={() => toggleService(svc.id)}
                        />
                        <label htmlFor={`svc-${svc.id}`} className="flex-1 cursor-pointer">
                          <span className="text-sm font-medium">{svc.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">£{svc.price?.toFixed(2)} · {svc.duration_minutes}min</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                );
              })}
              {services.length === 0 && <p className="text-sm text-muted-foreground">No services found. Add services first.</p>}
            </TabsContent>

            {/* ── Portfolio Tab ── */}
            <TabsContent value="portfolio" className="mt-0">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-muted-foreground">Add photos of work to display on this team member's public profile</p>
                <Button
                  variant="outline" size="sm" className="rounded-xl"
                  onClick={() => portfolioRef.current?.click()}
                  disabled={uploadingPortfolio}
                >
                  <Camera size={13} className="mr-1" />
                  {uploadingPortfolio ? 'Uploading...' : 'Add Photo'}
                </Button>
                <input
                  ref={portfolioRef}
                  type="file" accept="image/*"
                  className="hidden"
                  onChange={e => { if (e.target.files?.[0]) uploadPortfolioPhoto(e.target.files[0]); }}
                />
              </div>

              {(form.portfolio || []).length === 0 ? (
                <div
                  className="border-2 border-dashed border-border rounded-2xl p-12 text-center cursor-pointer hover:bg-muted/10 transition-colors"
                  onClick={() => portfolioRef.current?.click()}
                >
                  <Camera size={32} className="mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground">Click to add portfolio photos</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {(form.portfolio || []).map(photo => (
                    <div key={photo.id} className="relative group rounded-xl overflow-hidden aspect-square">
                      <img src={photo.photo_url} alt={photo.caption || 'Portfolio'} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button
                          onClick={() => removePortfolioPhoto(photo.id)}
                          className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => portfolioRef.current?.click()}
                    className="aspect-square border-2 border-dashed border-border rounded-xl flex items-center justify-center hover:bg-muted/10 transition-colors"
                  >
                    <Plus size={20} className="text-muted-foreground" />
                  </button>
                </div>
              )}
            </TabsContent>

            {/* ── Schedule Tab ── */}
            <TabsContent value="schedule" className="mt-0">
              <div className="space-y-2">
                {DAYS.map(day => (
                  <div key={day} className="flex items-center gap-3">
                    <Switch checked={form.schedule?.[day]?.enabled ?? false} onCheckedChange={v => updateScheduleDay(day, 'enabled', v)} />
                    <span className="w-12 text-xs capitalize font-medium">{day.slice(0, 3)}</span>
                    <Input type="time" value={form.schedule?.[day]?.start || '09:00'} onChange={e => updateScheduleDay(day, 'start', e.target.value)} className="w-28 h-8 rounded-lg text-xs" disabled={!form.schedule?.[day]?.enabled} />
                    <span className="text-xs text-muted-foreground">to</span>
                    <Input type="time" value={form.schedule?.[day]?.end || '17:00'} onChange={e => updateScheduleDay(day, 'end', e.target.value)} className="w-28 h-8 rounded-lg text-xs" disabled={!form.schedule?.[day]?.enabled} />
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setDialog(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={save} className="rounded-xl">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
